// Native EventSource owns parsing and reconnection; each hint asks the original read.
import { useEffect } from "react";

export function useReadEvents(address: string | null, admitted: boolean, refreshRead: () => void) {
  useEffect(() => {
    if (!address || !admitted || typeof EventSource === "undefined") return;
    let source: EventSource | null = null;
    let pending: ReturnType<typeof setTimeout> | undefined;
    let active = true;
    const refresh = () => {
      if (!active || document.hidden) return;
      clearTimeout(pending);
      // Coalesce commit bursts, including our own already-invalidated write.
      pending = setTimeout(() => {
        pending = undefined;
        if (active) refreshRead();
      }, 150);
    };
    const close = () => {
      source?.removeEventListener("ready", refresh);
      source?.removeEventListener("refresh", refresh);
      source?.removeEventListener("error", refresh);
      source?.close();
      source = null;
      clearTimeout(pending);
    };
    const connect = () => {
      if (!active || document.hidden || source) return;
      // Session cookies stay on the original same-origin BFF; no bearer URL.
      source = new EventSource(address);
      source.addEventListener("ready", refresh);
      source.addEventListener("refresh", refresh);
      // EventSource cannot inspect a refusal body. The ordinary read can surface it
      // and disable this connection rather than retrying an expired session forever.
      source.addEventListener("error", refresh);
    };
    const visibility = () => {
      if (document.hidden) close();
      else connect(); // ready refresh covers commits while this tab was hidden.
    };
    connect();
    document.addEventListener("visibilitychange", visibility);
    return () => {
      active = false;
      close();
      document.removeEventListener("visibilitychange", visibility);
    };
  }, [address, admitted, refreshRead]);
}
